import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import UserModal from './UserModal';
import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface RFIDTag {
  _id: string;
  tagId: string;
  userId: string;
  status: string;
  expiryDate: string;
}

interface User {
  _id: string;
  username: string;
  email: string;
  name: string;
  phone?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

const UserList: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [userRfidTags, setUserRfidTags] = useState<RFIDTag[]>([]);
  const [showRfidTagsModal, setShowRfidTagsModal] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
      setError(null);
    } catch (err: any) {
      setError(t('users.messages.fetchError'));
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleModalSave = () => {
    fetchUsers();
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleStatusToggle = async (userId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await api.put(`/users/${userId}`, { status: newStatus });
      fetchUsers();
    } catch (err) {
      setError(t('users.messages.updateError'));
    }
  };

  const handleDeleteClick = async (userId: string) => {
    try {
      setDeleteUserId(userId);
      const response = await api.get(`/rfid-tags?userId=${userId}`);
      const tags = response.data;
      
      if (tags.length > 0) {
        setUserRfidTags(tags);
        setShowRfidTagsModal(true);
      } else {
        setShowDeleteModal(true);
      }
      setDeleteError(null);
    } catch (err) {
      setDeleteError(t('users.messages.deleteError'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUserId) return;

    try {
      await api.delete(`/users/${deleteUserId}`);
      setShowDeleteModal(false);
      setDeleteUserId(null);
      fetchUsers();
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || t('users.messages.deleteError'));
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    try {
      setDeletingTagId(tagId);
      await api.delete(`/rfid-tags/${tagId}`);
      
      // Update the local state to remove the deleted tag
      setUserRfidTags(prevTags => prevTags.filter(tag => tag._id !== tagId));
      
      // If no more tags, close the modal and proceed with user deletion
      if (userRfidTags.length === 1) {
        const userId = deleteUserId;
        setShowRfidTagsModal(false);
        if (userId) {
          setDeleteUserId(userId);
          setShowDeleteModal(true);
        }
      }
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Failed to delete RFID tag');
    } finally {
      setDeletingTagId(null);
    }
  };

  const DeleteConfirmationModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg w-full max-w-md">
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
            <h2 className="text-xl font-bold">{t('users.actions.delete')}</h2>
          </div>
          
          <p className="mb-4">{t('users.messages.deleteConfirmation')}</p>
          
          {deleteError && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              {deleteError}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteUserId(null);
                setDeleteError(null);
              }}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('users.actions.delete')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const RFIDTagsModal = () => {
    if (!showRfidTagsModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg w-full max-w-md">
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-2" />
            <h2 className="text-xl font-bold">{t('users.rfidTags.title')}</h2>
          </div>
          
          <p className="mb-4">{t('users.messages.rfidTagsWarning')}</p>
          
          {deleteError && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              {deleteError}
            </div>
          )}

          <div className="mb-4 max-h-60 overflow-y-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left">{t('users.rfidTags.columns.tagId')}</th>
                  <th className="px-4 py-2 text-left">{t('users.rfidTags.columns.status')}</th>
                  <th className="px-4 py-2 text-left">{t('users.rfidTags.columns.expiryDate')}</th>
                  <th className="px-4 py-2 text-left">{t('users.rfidTags.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {userRfidTags.map(tag => (
                  <tr key={tag._id} className="border-b">
                    <td className="px-4 py-2">{tag.tagId}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded text-sm ${
                        tag.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {tag.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {new Date(tag.expiryDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleDeleteTag(tag._id)}
                        disabled={deletingTagId === tag._id}
                        className={`text-red-600 hover:text-red-900 ${
                          deletingTagId === tag._id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {deletingTagId === tag._id ? (
                          t('users.messages.deleting')
                        ) : (
                          <TrashIcon className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between mt-4">
            <button
              onClick={() => navigate('/operator/rfid-tags')}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('users.rfidTags.actions.manage')}
            </button>
            <button
              onClick={() => {
                setShowRfidTagsModal(false);
                setUserRfidTags([]);
                setDeleteError(null);
              }}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('users.rfidTags.actions.close')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div>{t('common.loading')}</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{t('users.title')}</h1>
        <button
          onClick={handleAddUser}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          {t('users.addUser')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-6 py-3 text-left">{t('users.columns.name')}</th>
              <th className="px-6 py-3 text-left">{t('users.columns.username')}</th>
              <th className="px-6 py-3 text-left">{t('users.columns.email')}</th>
              <th className="px-6 py-3 text-left">{t('users.columns.phone')}</th>
              <th className="px-6 py-3 text-left">{t('users.columns.status')}</th>
              <th className="px-6 py-3 text-left">{t('users.columns.createdAt')}</th>
              <th className="px-6 py-3 text-left">{t('users.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className="border-b">
                <td className="px-6 py-4">{user.name}</td>
                <td className="px-6 py-4">{user.username}</td>
                <td className="px-6 py-4">{user.email}</td>
                <td className="px-6 py-4">{user.phone || '-'}</td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 rounded ${
                      user.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {t(`users.status.${user.status}`)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleEditUser(user)}
                    className="text-blue-600 hover:text-blue-900 mr-2"
                  >
                    {t('users.actions.edit')}
                  </button>
                  <button
                    onClick={() => handleStatusToggle(user._id, user.status)}
                    className={`${
                      user.status === 'active' ? 'text-red-600' : 'text-green-600'
                    } hover:text-opacity-75 mr-2`}
                  >
                    {user.status === 'active' ? t('users.actions.deactivate') : t('users.actions.activate')}
                  </button>
                  <button
                    onClick={() => handleDeleteClick(user._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    {t('users.actions.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        user={selectedUser}
      />

      <DeleteConfirmationModal />
      <RFIDTagsModal />
    </div>
  );
};

export default UserList; 